<template>
<!-- The rail is OPTIONAL, and its absence is a statement rather than a simplification. It greys the
     ground the wizard will not accept and names the two ends of what it will — which only means
     something where the figure is bounded. A wizard whose revenue resizes both axes of every curve
     has no such bound: every rule the corridor is made of is a ratio, so nothing binds at any level,
     and a rail drawn there would be greying ground that does not exist. Such a caller passes no
     range and the figure above stands on its own.

     It is a component of its own because two boxes now carry one: the Total Revenue card the clone
     wizard draws, and the composer's opening statement, where the same figure is asked for beside
     the attribution. Copied into both, a change to how a limit is named would be a change made
     twice — and the second one is the one that gets forgotten. -->
<div v-if="range && axis" class="clone-range">
  <div class="clone-range__ticks" :style="{ height: ticksHeight }">
    <button
      v-for="entry in range.marks"
      :key="`${idPrefix}_tick_${entry.key}`"
      type="button"
      class="clone-range__tick"
      :style="entry.labelStyle"
      :title="entry.title"
      @click="$emit('jump', entry.level)">
      <span class="clone-range__tick-dot"></span>{{ entry.label }}
    </button>
  </div>
  <div class="clone-range__ground">
    <span
      v-for="entry in range.blocked"
      :key="`${idPrefix}_blocked_${entry.key}`"
      class="clone-range__blocked"
      :style="entry.style"></span>
    <v-slider
      class="clone-range__slider"
      :value="slider.value"
      :min="slider.min"
      :max="slider.max"
      :step="slider.step"
      :color="color"
      :track-color="trackColor"
      hide-details
      dense
      @input="$emit('slide', $event)"
      @change="$emit('commit', $event)"
    ></v-slider>
  </div>
  <div class="clone-range__axis">
    <span>{{ axis.from }}</span>
    <span class="clone-range__hint">{{ axis.hint }}</span>
    <span>{{ axis.to }}</span>
  </div>
</div>
</template>

<script>
// The log track under a figure that is read in multiples. It owns no arithmetic at all: every mark
// arrives placed, every blocked span arrives as a style, and both ends of the axis arrive formatted.
// See EkrmChannelCard for why.
export default {
  name: 'EkrmLevelRail',
  props: {
    // { marks, blocked } or null for a figure nothing bounds — see the template.
    range: { type: Object, default: null },
    slider: { type: Object, default: null },
    axis: { type: Object, default: null },
    ticksHeight: { type: String, default: '0px' },
    color: { type: String, default: '#43A047' },
    trackColor: { type: String, default: 'rgba(67, 160, 71, 0.18)' },
    // Two rails on one page would otherwise key their marks alike.
    idPrefix: { type: String, default: 'rail' }
  }
}
</script>

<style scoped>
  /* The stretch the wizard will accept, under the figure that reads it. A track rather than a chart:
     the figure is linear in the factor behind it, so there is no shape worth plotting — only a
     corridor and where the plan stands in it. What a trade-off chart greys over in two dimensions,
     this greys over in one, and it is the slider's own track it greys: the corridor is the pale
     colour the slider is dressed in, the plan is its thumb. */
  .clone-range {
    margin-top: 10px;
  }
  /* The bed the blocked ground and the slider share. Its box is the slider's, the slider being its
     only child in flow, so a percentage here is a percentage of the track. */
  .clone-range__ground {
    position: relative;
    margin-top: -6px;
  }
  /* The ground outside what the wizard will accept, the same grey the chart's own blocked ground
     carries. Taller than the 2px track it covers, so it still reads where the fill runs through it,
     and deaf to the pointer so the slider under it keeps every click. */
  .clone-range__blocked {
    position: absolute;
    top: 50%;
    height: 8px;
    margin-top: -4px;
    border-radius: 2px;
    background-color: rgba(0, 0, 0, 0.12);
    pointer-events: none;
  }
  /* The name of a limit, centred over it and clickable, for the reason the chart's own names are:
     a boundary the slider silently stops at is one the user has to find by pushing against it. */
  .clone-range__ticks {
    position: relative;
    height: 15px;
    margin-top: 2px;
  }
  .clone-range__tick {
    position: absolute;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 0;
    border: none;
    background: none;
    font-size: 0.62rem;
    line-height: 15px;
    white-space: nowrap;
    color: rgba(0, 0, 0, 0.5);
    cursor: pointer;
  }
  .clone-range__tick:hover,
  .clone-range__tick:focus {
    color: rgba(0, 0, 0, 0.85);
    text-decoration: underline;
    outline: none;
  }
  /* A limit is a line and not a point, and its glyph says so — the same glyph the chart's own limits
     carry. */
  .clone-range__tick-dot {
    width: 2px;
    height: 9px;
    flex: none;
    background-color: rgba(0, 0, 0, 0.35);
  }
  .clone-range__slider >>> .v-slider {
    margin: 0;
  }
  .clone-range__axis {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 8px;
    font-size: 0.65rem;
    color: rgba(0, 0, 0, 0.45);
    margin-top: -4px;
  }
  .clone-range__hint {
    color: rgba(0, 0, 0, 0.35);
  }
</style>
